import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Plus, Building2, Search, Trash2, Edit, Loader2,
  FileText, Users, FolderKanban, Filter,
} from 'lucide-react';
import { ExportDropdown } from '../shared/PageActions';
import ImportDialog from '../shared/ImportDialog';
import { getDefaultCompanyInfo } from '../../lib/brandConfig';
import { previewClientPdf, downloadClientPdf } from '../../lib/infoCardPdfExport';
import { API } from '../../lib/api';
import axios from 'axios';
import { Upload } from 'lucide-react';

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    lead:      { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' },
    active:    { bg: '#d1fae5', color: '#065f46', border: '#10b981' },
    inactive:  { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  };
  const s = styles[status] || styles.lead;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}33` }}
    >
      {status}
    </span>
  );
}

// ── Industry badge ────────────────────────────────────────────────────────────
function IndustryBadge({ industry }) {
  if (!industry) return null;
  const styles = {
    hospitality: { bg: '#ede9fe', color: '#5b21b6' },
    retail:      { bg: '#fce7f3', color: '#9d174d' },
    commercial:  { bg: '#dbeafe', color: '#1e40af' },
    other:       { bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = styles[industry] || styles.other;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
      style={{ background: s.bg, color: s.color }}
    >
      {industry}
    </span>
  );
}

// ── KPI bar ──────────────────────────────────────────────────────────────────
function KPIBar({ clients }) {
  const active = clients.filter(c => c.status === 'active').length;
  const leads  = clients.filter(c => c.status === 'lead').length;
  const openQuotes = clients.reduce((sum, c) => sum + (c.open_quotes_count || 0), 0);

  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {[
        { label: 'Active Clients', value: active, color: '#10b981' },
        { label: 'Leads', value: leads, color: '#f59e0b' },
        { label: 'Open Quotes', value: openQuotes, color: '#3b82f6' },
      ].map(kpi => (
        <div
          key={kpi.label}
          className="flex items-center gap-2 px-3 py-2 rounded-md"
          style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}
        >
          <span className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: kpi.color }}>
            {kpi.value}
          </span>
          <span className="text-xs text-[#6b7280]">{kpi.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── New client modal ─────────────────────────────────────────────────────────
function NewClientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: '', trading_name: '', abn: '',
    industry: '', status: 'lead', address: '', website: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.company_name.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/clients`, form);
      onCreated(res.data);
      setForm({
        company_name: '', trading_name: '', abn: '',
        industry: '', status: 'lead', address: '', website: '', notes: '',
      });
      onClose();
    } catch (err) {
      console.error('Failed to create client:', err);
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: '#e5e4df' }}>
          <h3 className="text-lg font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            New Client
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <Field label="Company Name *" value={form.company_name} onChange={v => handleChange('company_name', v)} />
          <Field label="Trading Name" value={form.trading_name} onChange={v => handleChange('trading_name', v)} />
          <Field label="ABN" value={form.abn} onChange={v => handleChange('abn', v)} />
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">Industry</label>
            <select
              value={form.industry}
              onChange={e => handleChange('industry', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={{ borderColor: '#e5e4df' }}
            >
              <option value="">— Select —</option>
              <option value="hospitality">Hospitality</option>
              <option value="retail">Retail</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Field label="Address" value={form.address} onChange={v => handleChange('address', v)} />
          <Field label="Website" value={form.website} onChange={v => handleChange('website', v)} />
          <Field label="Notes" value={form.notes} onChange={v => handleChange('notes', v)} multiline />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: '#e5e4df' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.company_name.trim()}
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Client
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded text-sm resize-none"
          style={{ borderColor: '#e5e4df' }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 border rounded text-sm"
          style={{ borderColor: '#e5e4df' }}
        />
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ClientsTab({ onEditClient }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterIndustry) params.set('industry', filterIndustry);
      if (searchQuery) params.set('search', searchQuery);
      const res = await axios.get(`${API}/clients?${params.toString()}`);
      setClients(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  }, [filterStatus, filterIndustry, searchQuery]);

  useEffect(() => {
    fetchClients().finally(() => setLoading(false));
  }, [fetchClients]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteClient = async (id) => {
    try {
      await axios.delete(`${API}/clients/${id}`);
      setClients(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  // ── Search debounce ──────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── PDF export ───────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();
  const handlePdfPreview = () => {
    // Preview first client if only one, or show message
    if (clients.length === 1) {
      previewClientPdf(clients[0], companyInfo);
    } else {
      // For multiple, preview the first one as sample
      previewClientPdf(clients[0], companyInfo);
    }
  };
  const handlePdfDownload = () => {
    // Download first client PDF; user can open detail for individual export
    if (clients.length > 0) {
      downloadClientPdf(clients[0], companyInfo);
    }
  };
  const handlePrint = () => window.print();

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* KPI */}
      <KPIBar clients={clients} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border rounded"
              style={{ borderColor: '#e5e4df' }}
            />
          </div>
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded"
            style={{ borderColor: '#e5e4df' }}
          >
            <option value="">All Status</option>
            <option value="lead">Lead</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {/* Industry filter */}
          <select
            value={filterIndustry}
            onChange={e => setFilterIndustry(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded hidden sm:block"
            style={{ borderColor: '#e5e4df' }}
          >
            <option value="">All Industry</option>
            <option value="hospitality">Hospitality</option>
            <option value="retail">Retail</option>
            <option value="commercial">Commercial</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
            title="Import clients from CSV/Excel"
          >
            <Upload className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button
            onClick={() => setShowNewModal(true)}
            size="sm"
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Client</span>
          </Button>
        </div>
      </div>

      {/* List */}
      {clients.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">No clients yet</p>
          <p className="text-[#6b7280]/60 text-sm mb-6">Create your first client to get started</p>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
            <Plus className="w-4 h-4 mr-2" />
            Create Client
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Desktop header */}
          <div
            className="hidden lg:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '1fr 100px 80px 120px 60px 60px 60px 100px' }}
          >
            <span>Company</span>
            <span>Industry</span>
            <span>Status</span>
            <span>ABN</span>
            <span className="text-center"><Users className="w-3.5 h-3.5 inline" /></span>
            <span className="text-center"><FileText className="w-3.5 h-3.5 inline" /></span>
            <span className="text-center"><FolderKanban className="w-3.5 h-3.5 inline" /></span>
            <span className="text-right">Actions</span>
          </div>

          {clients.map(c => (
            <div key={c.id}>
              {/* Desktop row */}
              <div
                className="hidden lg:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                style={{
                  gridTemplateColumns: '1fr 100px 80px 120px 60px 60px 60px 100px',
                  borderBottom: '1px solid rgba(201,168,76,0.1)',
                }}
                onClick={() => onEditClient(c.id)}
              >
                <div>
                  <span className="text-sm font-semibold text-[#1a1a2e]">{c.company_name}</span>
                  {c.trading_name && <span className="text-xs text-[#6b7280] ml-2">({c.trading_name})</span>}
                </div>
                <IndustryBadge industry={c.industry} />
                <StatusBadge status={c.status} />
                <span className="text-xs font-mono text-[#6b7280]">{c.abn || '—'}</span>
                <span className="text-xs text-center text-[#6b7280]">{c.active_contacts_count ?? 0}</span>
                <span className="text-xs text-center text-[#6b7280]">{c.open_quotes_count ?? 0}</span>
                <span className="text-xs text-center text-[#6b7280]">{c.active_projects_count ?? 0}</span>
                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => onEditClient(c.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete Client</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {c.company_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteClient(c.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Mobile card */}
              <div
                className="lg:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                onClick={() => onEditClient(c.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-sm font-semibold text-[#1a1a2e]">{c.company_name}</span>
                    {c.trading_name && <span className="text-xs text-[#6b7280] ml-1">({c.trading_name})</span>}
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <IndustryBadge industry={c.industry} />
                  {c.abn && <span className="text-xs font-mono text-[#9ca3af]">ABN {c.abn}</span>}
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-[#6b7280]">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.active_contacts_count ?? 0}</span>
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{c.open_quotes_count ?? 0}</span>
                  <span className="flex items-center gap-1"><FolderKanban className="w-3 h-3" />{c.active_projects_count ?? 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New client modal */}
      <NewClientModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={(newClient) => {
          setClients(prev => [newClient, ...prev]);
        }}
      />

      {/* Import modal */}
      <ImportDialog
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Clients"
        type="clients"
        onImported={(res) => {
          if (res.success > 0) fetchClients();
        }}
      />
    </>
  );
}
